import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { experienceData } from '../data/experience';
import './Experience.css';

const Experience = () => {
  const data = experienceData;
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!sectionRef.current || !timelineRef.current) return;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const section = sectionRef.current;
          const timeline = timelineRef.current;
          
          if (!section || !timeline) return;
          
          // Get section and timeline positions
          const timelineRect = timeline.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          
          // Calculate when timeline starts and ends relative to viewport
          const timelineTop = timelineRect.top;
          const timelineBottom = timelineRect.bottom;
          const timelineHeight = timelineRect.height;
          
          let progress = 0;
          
          // Check if timeline is in viewport
          if (timelineBottom > 0 && timelineTop < windowHeight) {
            // Calculate progress: how much of the timeline has been scrolled past
            const scrollableDistance = timelineHeight + windowHeight;
            const scrolledDistance = windowHeight - timelineTop;
            
            progress = Math.max(0, Math.min(100, (scrolledDistance / scrollableDistance) * 100));
          } else if (timelineTop >= windowHeight) {
            // Timeline hasn't been reached yet
            progress = 0;
          } else {
            // Timeline has been completely scrolled past
            progress = 100;
          }
          
          setScrollProgress(progress);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll, { passive: true });
      window.removeEventListener('resize', handleScroll, { passive: true });
    };
  }, []);

  return (
    <section id="experience" ref={sectionRef} className="experience-section">
      <div className="section-heading fade-in-up">
        <h2>{data.heading}</h2>
        <p>{data.description}</p>
      </div>
      <div ref={timelineRef} className="experience-timeline">
        {/* Scroll indicator dot */}
        <div 
          className="timeline-scroll-indicator"
          style={{ top: `${scrollProgress}%` }}
        ></div>
        {data.experiences.map((exp, index) => (
          <div key={exp.id} className={`timeline-item fade-in-up ${index % 2 === 0 ? 'left' : 'right'}`}>
            <div className="timeline-marker"></div>
            <div className="timeline-content">
              <div className="experience-header">
                <h3 className="experience-position">{exp.position}</h3>
                <h4 className="experience-company">{exp.company}</h4>
                <div className="experience-meta">
                  <span className="experience-duration">
                    <FaCalendarAlt /> {exp.duration}
                  </span>
                  <span className="experience-location">
                    <FaMapMarkerAlt /> {exp.location}
                  </span>
                </div>
              </div>
              <p className="experience-description">{exp.description}</p>
              <ul className="experience-achievements">
                {exp.achievements.map((achievement, idx) => (
                  <li key={idx}>{achievement}</li>
                ))}
              </ul>
              <div className="experience-technologies">
                {exp.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Experience;

